import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  applyThemeToDocument,
  getThemeStorage,
  isValidTheme,
  persistTheme,
  readStoredTheme,
  readThemePreference,
  SITE_THEME_STORAGE_KEY,
  DEFAULT_THEME,
  ThemeContext,
  THEME_STORAGE_KEY
} from './ThemeContext';

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => (
    typeof window === 'undefined' ? 'dark' : readStoredTheme(getThemeStorage(window))
  ));
  const explicitTheme = useRef(typeof window === 'undefined' ? null : readThemePreference(getThemeStorage(window)));
  const siteTheme = useRef(typeof window === 'undefined' ? DEFAULT_THEME : readThemePreference(getThemeStorage(window), SITE_THEME_STORAGE_KEY) || DEFAULT_THEME);
  const currentTheme = useRef(theme);

  useEffect(() => {
    currentTheme.current = theme;
    applyThemeToDocument(document, theme);
  }, [theme]);

  const setTheme = useCallback((newTheme) => {
    if (isValidTheme(newTheme)) {
      explicitTheme.current = newTheme;
      currentTheme.current = newTheme;
      persistTheme(getThemeStorage(window), newTheme);
      setThemeState(newTheme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(currentTheme.current === 'dark' ? 'light' : 'dark');
  }, [setTheme]);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    let active = true;
    fetch('/api/site-settings', { signal: controller.signal, cache: 'no-store' })
      .then(async response => {
        if (!response.ok) throw new Error(`Theme settings HTTP ${response.status}`);
        const data = await response.json();
        if (!data.success) throw new Error('Theme settings unavailable');
        const next = isValidTheme(data.settings?.default_theme) ? data.settings.default_theme : DEFAULT_THEME;
        if (!active) return;
        siteTheme.current = next;
        try { getThemeStorage(window)?.setItem(SITE_THEME_STORAGE_KEY, next); } catch { /* Storage may be blocked. */ }
        // A late response must never overwrite an explicit toggle.
        if (!explicitTheme.current) setThemeState(next);
      })
      .catch(error => {
        if (active) console.warn('Výchozí motiv webu se nepodařilo načíst; zachovávám aktuální motiv.', error);
      })
      .finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) {
        explicitTheme.current = isValidTheme(event.newValue) ? event.newValue : null;
        setThemeState(explicitTheme.current || siteTheme.current);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const value = useMemo(() => ({
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  }), [theme, setTheme, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
